import { axiosRequest } from '@/plugins/axios-common'

import { handleError } from '@/store/handle-error'

import { RECORD_NAMESPACE } from '@/store/record'

import { CloneLegalCaseActions } from './partials/clone'
import { ClosingWizardActions } from './partials/closing-wizard'
import { DocketEntriesActions } from './partials/docket-entries'
import { DocketEntriesInfoActions } from './partials/docket-entries-info'
import { ECOPortalIntegrationActions } from './partials/eco-portal-integration'
import { EngagementLetterActions } from './partials/engagement-letter'
import { LegalCaseEditActions } from './partials/edit'
import { LegalCaseTagActions } from './partials/tag'
import { MyLegalCasesActions } from './partials/my-legal-cases'
import { ReopeningWizardActions } from './partials/reopening-wizard'

export const LegalCaseActions = {
  ...CloneLegalCaseActions,
  ...ClosingWizardActions,
  ...DocketEntriesActions,
  ...DocketEntriesInfoActions,
  ...EngagementLetterActions,
  ...ECOPortalIntegrationActions,
  ...LegalCaseEditActions,
  ...LegalCaseTagActions,
  ...MyLegalCasesActions,
  ...ReopeningWizardActions,

  associateParties: async ({ commit }, requestData) => {
    return axiosRequest.
      post(`/legal_cases/${requestData.id}/associate_parties`, { party_ids: requestData.partyIds }).
      then(({ data }) => {
        commit('setLegalCaseAssociatedParties', data.parties || [])
      }).
      catch(handleError)
  },

  downloadLegalCaseAttachments: async ({ commit }, legalCaseId) => {
    return axiosRequest.
      get(`legal_cases/${legalCaseId}/download_attachments`).
      then(({ data }) => commit('downloads/addDownloadRequest', data.download_id, { root: true })).
      catch(handleError)
  },

  getLegalCaseUnassociatedParties: async ({ commit }, requestData) => {
    commit('setSearchLoading', true)

    return axiosRequest.
      get(`/legal_cases/${requestData.id}/unassociated_parties`, { params: requestData.params }).
      then(({ data }) => {
        commit('setLegalCaseId', requestData.id)
        commit('setLegalCaseUnassociatedParties', data.unassociated_parties || {})
        commit('setSearchLoading', false)
      }).
      catch(handleError)
  },

  partyHasScheduledTextMessages: async (_, { legalCaseId, partyId }) => {
    return axiosRequest.
      get(`/legal_cases/${legalCaseId}/parties/${partyId}/has_scheduled_text_messages`).
      then(({ data }) => data.has_scheduled_text_messages).
      catch(handleError)
  },

  getLegalCase: async ({ commit }, caseId) => {
    return axiosRequest.
      get(`/legal_cases/${caseId}`).
      then(({ data }) => {
        if (data.legal_case) {
          commit('setLegalCase', data.legal_case)
          return data.legal_case
        }
      }).
      catch(handleError)
  },

  fetchLegalCase: async ({ commit }, legalCaseId) => {
    return axiosRequest.
      get(`/legal_cases/${legalCaseId}`).
      then(({ data }) => {
        if (data.legal_case) { commit('storeLegalCase', data.legal_case) }
      }).
      catch(handleError)
  },

  getLegalCaseOptionsList: async ({ commit }, requestData) => {
    return axiosRequest.
      get(`/records/${requestData.id}/legal_cases/options`).
      then(({ data }) => commit('setLegalCaseOptionsList', data.legal_cases)).
      catch(handleError)
  },

  createLegalCase: async ({ commit }, caseData) => {
    return axiosRequest.
      post('/legal_cases', {
        legal_case: caseData,
      }).
      then(({ data }) => {
        if (data.legal_case) {
          commit('setLegalCase', data.legal_case)
        }

        return data
      }).
      catch(handleError)
  },

  updateLegalCase: async ({ commit }, requestData) => {
    return axiosRequest.
      patch(`/legal_cases/${requestData.caseId}`, {
        legal_case: requestData.caseData,
      }).
      then(({ data }) => {
        commit('setLegalCase', data.legal_case)
      }).
      catch(handleError)
  },

  destroyLegalCase: async ({ commit }, { caseId, reason }) => {
    return axiosRequest.
      delete(`/legal_cases/${caseId}`, { data: { reason } }).
      then(_ => {
        commit('removeLegalCase', { id: caseId })
      }).
      catch(handleError)
  },

  removePartyFromLegalCase: async ({ commit }, requestData) => {
    return axiosRequest.
      delete(`legal_cases/${requestData.legal_case_id}/parties/${requestData.party_id}`).
      then(({ data }) => {
        commit('setLegalCase', data.legal_case)
        commit(`${RECORD_NAMESPACE}/updateRecord`, {
          data: data.legal_case.record.contact_party_id,
          field: 'contact_party_id',
        }, { root: true })
      }).
      catch(handleError)
  },

  resetCurrentLegalCase ({ commit }) {
    commit('setCurrentLegalCaseId', null)
    commit('setLegalCase', [])
  },

  setCurrentLegalCaseId ({ commit }, value) {
    commit('setCurrentLegalCaseId', value)
  },

  setCurrentLegalCase ({ commit }, value) {
    commit('setCurrentLegalCase', value)
  },

  setLegalCase ({ commit }, value) {
    commit('setLegalCase', value)
  },

  setPaginationOptions ({ commit }, options) {
    commit('setPaginationOptions', options)
  },

  setShowAssociatePartyDialog ({ commit }, visible) {
    commit('setShowAssociatePartyDialog', visible)
  },

  updatePartyRoleInCase: async ({ commit }, requestData) => {
    return axiosRequest.
      patch('/update_role', requestData).
      then(({ data }) => {
        commit('setLegalCase', data.legal_case)
      }).
      catch(handleError)
  },

  async loadLegalCasesPartySliderSettings ({ commit }) {
    return axiosRequest.
      get('/legal_cases/party_slider_settings').
      then(({ data }) => commit('setLegalCasesPartySliderSettings', data)).
      catch(handleError)
  },

  async updateLegalCaseChecklist ({ commit }, { checklist, legalCaseId }) {
    return axiosRequest.
      put(`/legal_cases/${legalCaseId}/checklist`, { checklist }).
      then(({ data }) => {
        commit('setLegalCase', data.legal_case)
        return data.legal_case.checklist
      }).
      catch(handleError)
  },
}
